import React from 'react';
import { TableContainer, Paper, Typography, Box, useTheme } from '@mui/material';
import '../styles/ProfessionalTable.css';

const ProfessionalTable = ({ 
  columns, 
  data, 
  onRowClick, 
  selectedRowId, 
  loading = false,
  emptyMessage = "No data available",
  className = "",
  ...props 
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const getCellValue = (row, column) => {
    if (typeof column.accessor === 'function') {
      return column.accessor(row);
    }
    return row[column.accessor];
  };

  const getCellClassName = (row, column) => {
    if (column.className) {
      return typeof column.className === 'function' 
        ? column.className(row) 
        : column.className;
    }
    return '';
  };

  const getCellStyle = (row, column) => {
    if (column.style) {
      return typeof column.style === 'function' 
        ? column.style(row) 
        : column.style;
    }
    return {};
  };

  if (loading) {
    return (
      <TableContainer 
        component={Paper} 
        elevation={0} 
        sx={(theme) => ({
          borderRadius: 2,
          overflow: 'hidden',
          background: theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.06)'
            : 'rgba(255,255,255,0.8)',
          backdropFilter: 'blur(10px)',
          border: theme.palette.mode === 'dark'
            ? '1px solid rgba(255,255,255,0.08)'
            : '1px solid rgba(255,255,255,0.2)',
        })}
      >
        <table className={`professional-table loading ${className}`}>
          <thead>
            <tr>
              {columns.map((column, index) => (
                <th key={index} style={column.headerStyle}>
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...Array(3)].map((_, index) => (
              <tr key={index}>
                {columns.map((_, colIndex) => (
                  <td key={colIndex}>
                    <Box sx={{ height: 20, bgcolor: 'grey.200', borderRadius: 1 }} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </TableContainer>
    );
  }

  if (!data || data.length === 0) {
    return (
      <TableContainer 
        component={Paper} 
        elevation={0} 
        sx={(theme) => ({
          borderRadius: 2,
          overflow: 'hidden',
          background: theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.06)'
            : 'rgba(255,255,255,0.8)',
          backdropFilter: 'blur(10px)',
          border: theme.palette.mode === 'dark'
            ? '1px solid rgba(255,255,255,0.08)'
            : '1px solid rgba(255,255,255,0.2)',
        })}
      >
        <div className="professional-table-empty">
          <div className="professional-table-empty-icon">📊</div>
          <Typography variant="body1" color="text.secondary">
            {emptyMessage}
          </Typography>
        </div>
      </TableContainer>
    );
  }

  return (
    <TableContainer 
      component={Paper} 
      elevation={0} 
      sx={(theme) => ({
        borderRadius: 2,
        overflow: 'auto',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        background: theme.palette.mode === 'dark'
          ? 'rgba(255,255,255,0.06)'
          : 'rgba(255,255,255,0.8)',
        backdropFilter: 'blur(10px)',
        border: theme.palette.mode === 'dark'
          ? '1px solid rgba(255,255,255,0.08)'
          : '1px solid rgba(255,255,255,0.2)',
        '&::-webkit-scrollbar': {
          height: '8px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.2)'
            : 'rgba(0,0,0,0.2)',
          borderRadius: '4px',
        },
      })}
    >
      <style>
        {`
          .professional-table tbody tr:hover {
            background-color: ${isDark ? 'rgba(102, 126, 234, 0.08)' : 'rgba(76, 175, 80, 0.12)'} !important;
            transform: translateY(-1px);
            box-shadow: ${isDark ? '0 2px 8px rgba(102, 126, 234, 0.15)' : '0 2px 8px rgba(76, 175, 80, 0.2)'} !important;
          }
          .professional-table tbody tr.selected {
            background-color: ${isDark ? 'rgba(102, 126, 234, 0.15)' : 'rgba(76, 175, 80, 0.15)'} !important;
            border-left: 4px solid ${isDark ? '#667eea' : '#4caf50'} !important;
          }
          .professional-table tbody tr.selected:hover {
            background-color: ${isDark ? 'rgba(102, 126, 234, 0.2)' : 'rgba(76, 175, 80, 0.2)'} !important;
          }
        `}
      </style>
      <table className={`professional-table ${className}`} style={{ minWidth: '600px' }} {...props}>
        <thead>
          <tr>
            {columns.map((column, index) => (
              <th 
                key={index} 
                style={column.headerStyle}
                className={column.sortable ? 'sortable-header' : ''}
                onClick={column.onSort ? () => column.onSort(column.accessor) : undefined}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIndex) => (
            <tr 
              key={rowIndex}
              className={selectedRowId && selectedRowId === row.id ? 'selected' : ''}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              style={{ cursor: onRowClick ? 'pointer' : 'default' }}
            >
              {columns.map((column, colIndex) => (
                <td 
                  key={colIndex}
                  className={getCellClassName(row, column)}
                  style={getCellStyle(row, column)}
                >
                  {column.render ? 
                    column.render(getCellValue(row, column), row) : 
                    getCellValue(row, column)
                  }
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </TableContainer>
  );
};

export default ProfessionalTable;
